// Hero.tsx — usa useLanguage() para todas las cadenas
import { useIntersectionObserver } from '@/shared/hooks/useIntersectionObserver';
import { useLanguage } from '@/shared/context/LanguageContext';
import { useTheme } from '@/shared/context/ThemeContext';
import { Button } from '@/shared/ui/Button/Button';
import {
  ArrowRightIcon,
  LightningIcon,
  CheckIcon,
  HeadsetIcon,
} from '@/shared/ui/Button/icons';
import GraphDecoration from './GraphDecoration';
import styles from './Hero.module.css';

export default function Hero() {
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    once: true,
  });

  const { t } = useLanguage();

  // Obtenemos el tema actual desde el mismo contexto
  // que utiliza el Header.
  const { theme } = useTheme();

  const h = t.hero;

  // Imagen diferente dependiendo del tema.
  // Los archivos vivían en frontend/reinvent-x-app/images/ (fuera de
  // public/, donde Vite realmente sirve los estáticos) -- por eso el Hero
  // apuntaba a dos rutas que daban 404. Ya están copiados a public/images/.
  const heroImage =
    theme === 'light'
      ? '/images/LandingPageDemoLight.png'
      : '/images/LandingPageDemoDark.png';

  return (
    <section
      className={styles.section}
      id="hero"
      aria-labelledby="hero-heading"
    >
      <div
        ref={ref}
        className={`${styles.grid} ${
          isVisible ? styles.visible : ''
        }`}
      >
        <div className={styles.left}>
          <div className={styles.textStack}>
            <div className={styles.h1Wrap}>
              <h1
                className={styles.h1}
                id="hero-heading"
              >
                {h.h1Pre}{' '}
                <span className={styles.highlight}>
                  {h.h1LandingPage}
                </span>{' '}
                {h.h1Mid} {h.h1b}
                {h.h1c}
              </h1>
            </div>

            <p
              className={styles.subtitle}
              dangerouslySetInnerHTML={{
                __html: h.subtitle,
              }}
            />

            <div
              className={styles.featureRow}
              aria-label="Credenciales"
            >
              {[
                {
                  Icon: LightningIcon,
                  text: h.cred1,
                },
                {
                  Icon: CheckIcon,
                  text: h.cred2,
                },
                {
                  Icon: HeadsetIcon,
                  text: h.cred4,
                },
              ].map(({ Icon, text }) => (
                <div
                  key={text}
                  className={styles.feature}
                >
                  <span
                    className={styles.featureIcon}
                    aria-hidden="true"
                  >
                    <Icon />
                  </span>

                  <span
                    className={styles.featureText}
                  >
                    {text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.ctaRow}>
            <Button
              variant="primary"
              size="lg"
              href="#form"
              icon={<ArrowRightIcon />}
            >
              {h.cta1}
            </Button>

            <Button
              variant="outline"
              size="lg"
              href="#services"
            >
              {h.cta2}
            </Button>
          </div>
        </div>

        <div className={styles.laptopWrap}>
          <div>
            <img
              src={heroImage}
              alt="Vista previa de los servicios: landing page, sistema, modernización y mantenimiento"
              loading="lazy"
            />
          </div>

          <GraphDecoration
            visible={isVisible}
          />
        </div>
      </div>

      <div
        className={styles.scrollHint}
        aria-hidden="true"
      >
        <span>{h.scrollHint}</span>
        <span>↓</span>
      </div>
    </section>
  );
}